/** @jsxImportSource @emotion/react */
'use client'

import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import Modal from '@mui/material/Modal'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useState } from 'react'
import type { LoginResponse } from '../../../../client'
import useAuth from '../../../hooks/useAuth'
import { sanitizeInternalRedirect } from '../../../utils/safeRedirect'
import LoginForm from './LoginForm'
import OtpForm from './OtpForm'
import RecoverPasswordForm from './RecoverPasswordForm'
import ShowcaseCarousel from './ShowcaseCarousel'
import { styles } from './styles'
import useRememberedEmail from './useRememberedEmail'

interface LoginModalProps {
  open: boolean
  onClose: () => void
}

const LoginModal: React.FC<LoginModalProps> = ({ open, onClose }) => {
  const { initialEmail, initialRemember, persist } = useRememberedEmail()

  const [username, setUsername] = useState(initialEmail)
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(initialRemember)
  const [otpCode, setOtpCode] = useState('')
  const [loginHistoryId, setLoginHistoryId] = useState<number | null>(null)
  const [userId, setUserId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [view, setView] = useState<'login' | 'recover'>('login')
  const router = useRouter()

  const {
    loginMutation,
    handleOtpSubmit,
    otpError,
    setOtpError,
    requiresOtp,
    error,
    resetOtpRequirement,
    resetError,
  } = useAuth()

  const redirectAfterAuth = () => {
    // Check if there's a redirect URL stored from the login flow.
    const redirectUrl = sanitizeInternalRedirect(
      localStorage.getItem('loginRedirectUrl'),
      window.location.origin,
    )
    if (redirectUrl) {
      localStorage.removeItem('loginRedirectUrl') // Clear the stored URL

      // Use a slightly longer delay to ensure the modal is fully closed
      setTimeout(() => {
        router.push(redirectUrl)
      }, 300)
    }
  }

  const handleLogin = async () => {
    if (!username.trim() || !password.trim()) {
      resetError('Please enter your email and password.')
      return
    }

    setIsLoading(true)
    try {
      resetError()
      const response: LoginResponse = await loginMutation.mutateAsync({
        username,
        password,
      })

      // Credentials were accepted (OTP is a second factor) — remember the email
      // only, never the password.
      persist(username, rememberMe)

      if (response.requiresOtp) {
        setLoginHistoryId(response.loginHistoryId ?? null)
        setUserId(response.userId ?? null)
      } else {
        onClose()
        redirectAfterAuth()
      }
    } catch (err: any) {
      const detail =
        err.response?.detail ||
        err.message || // Extract detail from the response or normalized error
        'An unexpected error occurred. Please try again.' // Fallback message

      // The generated client surfaces a bare "Validation Error" for 422s; make
      // it relevant to this form.
      const errorMessage =
        detail === 'Validation Error'
          ? 'Please check your email and password and try again.'
          : detail

      resetError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleOtpSubmission = async () => {
    if (!otpCode.trim()) {
      setOtpError('Please enter your one-time passcode.')
      return
    }

    if (!loginHistoryId || !userId) {
      console.warn('Missing loginHistoryId or userId during OTP submission')
      setOtpError('Invalid login attempt. Please try logging in again.')
      return
    }

    setIsLoading(true)
    try {
      resetError()
      await handleOtpSubmit(userId, otpCode, loginHistoryId)
      if (!otpError) {
        onClose()
        redirectAfterAuth()
      }
    } catch (err) {
      console.error('OTP submission failed:', err)
      setOtpError('OTP submission failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Clean up any stored redirect URL when the modal is closed manually.
  const handleModalClose = () => {
    // Only clear the redirectUrl if we're not actively redirecting. This
    // prevents race conditions when manually closing after successful login.
    if (!isLoading) {
      localStorage.removeItem('loginRedirectUrl')
    }
    setView('login')
    onClose()
  }

  const handleForgotPassword = () => setView('recover')

  const ariaLabel = requiresOtp
    ? 'Enter your one-time passcode'
    : view === 'recover'
      ? 'Recover your password'
      : 'Sign in'

  return (
    <Modal open={open} onClose={handleModalClose}>
      <div
        css={styles.shell}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
      >
        <IconButton
          aria-label="Close"
          onClick={handleModalClose}
          css={styles.closeButton}
        >
          <CloseIcon />
        </IconButton>

        <div css={styles.formColumn}>
          <div css={styles.brand}>
            <Image
              src="/assets/images/WH-logo.png"
              alt="WyseHub logo"
              width={150}
              height={23}
              priority
              css={styles.logo}
            />
            <span css={styles.logoDivider} aria-hidden="true" />
            <Image
              src="/AiClaimsBlackLogo.svg"
              alt="AI Claims logo"
              width={150}
              height={25}
              priority
              css={styles.logoAiClaims}
            />
          </div>
          <div css={styles.formBody}>
            {requiresOtp ? (
              <OtpForm
                otpCode={otpCode}
                onOtpChange={setOtpCode}
                onSubmit={handleOtpSubmission}
                onBack={resetOtpRequirement}
                otpError={otpError}
                isLoading={isLoading}
              />
            ) : view === 'recover' ? (
              <RecoverPasswordForm
                initialEmail={username}
                onBack={() => setView('login')}
              />
            ) : (
              <LoginForm
                email={username}
                password={password}
                onEmailChange={setUsername}
                onPasswordChange={setPassword}
                rememberMe={rememberMe}
                onRememberMeChange={setRememberMe}
                onSubmit={handleLogin}
                onForgotPassword={handleForgotPassword}
                error={error}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>

        <div css={styles.showcaseColumn}>
          <ShowcaseCarousel />
        </div>
      </div>
    </Modal>
  )
}

export default LoginModal
